import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Api from "../../Api";

// Cache implementation
const apiCache = {};
const cacheTTL = 300000; // 5 minutes

const LinkSelectField = ({
    hidden,
    placeholder,
    register,
    errors,
    label,
    name,
    rules,
    apiConfig,
    fallbackOptions,
    errorMessage,
    defaultValue,
    setValue,
    onChange,
    useSearch = false,
    onSearchInputChange,
    className,
    styles = {},
    labelClassName,
    labelStyles,
    multiSelect = false,
    watch,
    searchField = 'summary',
}) => {
    const [options, setOptions] = useState(fallbackOptions || []);
    const [loading, setLoading] = useState(useSearch ? false : true);
    const [apiError, setApiError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [usingFallback, setUsingFallback] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);
    const maxRetries = 3;
    const retryDelay = 2000;
    const searchDebounceDelay = 500;

    // Generate search params with search term
    const getSearchParams = useCallback(() => {
        const baseParams = apiConfig?.requestParams || {};

        if (useSearch && searchTerm) {
            // Add search parameter only when user has typed something
            return {
                ...baseParams,
                [searchField]: searchTerm
            };
        }

        return baseParams;
    }, [apiConfig, searchTerm, useSearch, searchField]);

    // Generate cache key from API config and search params
    const generateCacheKey = useCallback(() => {
        if (!apiConfig) return null;

        const params = getSearchParams();
        const paramsString = Object.entries(params)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        return `${apiConfig.endpoint}|${paramsString}`;
    }, [apiConfig, getSearchParams]);

    // Check if cache is valid
    const isCacheValid = useCallback((cacheKey) => {
        if (!apiCache[cacheKey]) return false;

        const cachedData = apiCache[cacheKey];
        return Date.now() - cachedData.timestamp < cacheTTL;
    }, []);

    // Fetch data from API
    const fetchData = useCallback(async (cacheKey) => {

        if (!apiConfig) {
            setLoading(false);
            return;
        }

        // Check cache first
        if (isCacheValid(cacheKey)) {
            const cachedData = apiCache[cacheKey];
            setOptions(cachedData.data);
            setLoading(false);
            setUsingFallback(false);
            return;
        }

        setLoading(true);
        setApiError(null);

        try {
            const { credentials } = apiConfig;
            const params = getSearchParams();

            // Make API request
            const { results } = await Api.dossiers.GetList(params, {
                apiUsername: credentials.username,
                apiKey: credentials.password
            });
            const { responseMapping } = apiConfig;

            // Process response according to mapping
            if (results && Array.isArray(results.dossiers)) {
                const mappedOptions = results.dossiers.map(item => ({
                    value: item[responseMapping.valueField],
                    label: item[responseMapping.labelField]
                }));

                // Cache the results
                if (cacheKey) {
                    apiCache[cacheKey] = {
                        data: mappedOptions,
                        timestamp: Date.now()
                    };
                }

                setOptions(mappedOptions);
                setUsingFallback(false);
            } else {
                throw new Error('Invalid API response format');
            }

        } catch (error) {
            console.error('API request failed:', error);

            if (retryCount < maxRetries - 1) {
                // Retry after delay
                setTimeout(() => {
                    setRetryCount(prev => prev + 1);
                }, retryDelay);
            } else {
                // Use fallback options if available
                if (fallbackOptions && fallbackOptions.length > 0) {
                    setOptions(fallbackOptions);
                    setUsingFallback(true);
                    setApiError(errorMessage || 'Failed to load options');
                } else {
                    setApiError(errorMessage || 'Failed to load options');
                }
            }
        } finally {
            setLoading(false);
        }
    }, [apiConfig, retryCount, fallbackOptions, errorMessage, isCacheValid, getSearchParams]);

    // Effect to fetch data when component mounts or API config changes (only for non-search fields)
    useEffect(() => {
        // If useSearch is enabled, load initial data WITHOUT search filter
        if (useSearch) {
            const baseParams = apiConfig?.requestParams || {};
            const paramsString = Object.entries(baseParams)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, value]) => `${key}=${value}`)
                .join('&');
            const cacheKey = baseParams && Object.keys(baseParams).length > 0
                ? `${apiConfig?.endpoint}|${paramsString}`
                : null;

            if (cacheKey) {
                setLoading(true);
                fetchData(cacheKey);
            } else if (fallbackOptions) {
                setOptions(fallbackOptions);
                setLoading(false);
            } else {
                setLoading(false);
            }
            return;
        }

        const cacheKey = generateCacheKey();

        if (cacheKey) {
            fetchData(cacheKey);
        } else if (fallbackOptions) {
            setOptions(fallbackOptions);
            setLoading(false);
        }
    }, [apiConfig, useSearch]);

    // Effect to clear cache when form resets and cleanup search timeout
    useEffect(() => {
        return () => {
            // Clear search timeout
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            // Clear cache when component unmounts (form reset)
            const cacheKey = generateCacheKey();
            if (cacheKey && apiCache[cacheKey]) {
                delete apiCache[cacheKey];
            }
        };
    }, [generateCacheKey, searchTimeout]);

    const handleReactSelectChange = (selected) => {
        // Always return an array for consistency, even for single select
        const value = multiSelect
            ? (selected ? selected.map(s => s.value) : [])
            : (selected ? [selected.value] : []);


        setValue(name, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });

        // Force re-render to update validation state
        setTimeout(() => {
            setValue(name, value, { shouldValidate: true });
        }, 0);

        if (onChange) {
            onChange(value);
        }
    };

    const handleInputChange = (inputValue) => {
        // Call parent callback if provided
        if (onSearchInputChange) {
            onSearchInputChange(inputValue);
        }

        // Only handle search if useSearch is enabled
        if (!useSearch) {
            return;
        }

        // Clear previous timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const trimmedInput = inputValue?.trim() || '';

        // Update search term immediately for UI feedback
        setSearchTerm(trimmedInput);

        // Set new timeout for API call (debounce)
        if (trimmedInput) {
            const newTimeout = setTimeout(() => {
                setLoading(true);
                // Fetch data will use the latest searchTerm from state via getSearchParams
                const baseParams = apiConfig?.requestParams || {};
                const params = {
                    ...baseParams,
                    [searchField]: trimmedInput
                };
                const paramsString = Object.entries(params)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&');
                const cacheKey = `${apiConfig.endpoint}|${paramsString}`;

                fetchData(cacheKey);
            }, searchDebounceDelay);

            setSearchTimeout(newTimeout);
        }
    };

    const handleRetry = () => {
        setRetryCount(0);
        const cacheKey = generateCacheKey();
        if (cacheKey) {
            fetchData(cacheKey);
        }
    };

    // Get current values from form state
    const currentValues = watch ? watch(name) : defaultValue;

    // Ensure currentValues is always treated as an array for consistency
    const normalizedCurrentValues = Array.isArray(currentValues)
        ? currentValues
        : (currentValues ? [currentValues] : []);

    // Find the current option object(s) for React Select
    const currentOption = multiSelect
        ? (options?.filter(opt => normalizedCurrentValues.includes(opt.value)) || [])
        : (options?.find((opt) => opt.value === normalizedCurrentValues[0]) || null);

    // Register the field with React Hook Form
    const { onChange: rhfOnChange, onBlur: rhfOnBlur, name: fieldName, ref: fieldRef } = register(name, rules);

    return (
        <div
            className={`${hidden ? 'hidden' : 'flex'
                } flex-col items-start w-full ${className}`}
        >
            <label
                htmlFor={name}
                className={`form-label font-bold float-start mb-1 ${labelClassName}`}
                style={labelStyles}
            >
                {rules?.required && <span className="text-red-500">*</span>} {label}
            </label>

            {apiError && (
                <div className="w-full p-2 mb-2 bg-yellow-100 border border-yellow-300 rounded-md flex justify-between items-center">
                    <span className="text-yellow-800 text-sm">{apiError}</span>
                    <button
                        type="button"
                        onClick={handleRetry}
                        className="text-yellow-800 hover:text-yellow-900 text-xs font-medium"
                    >
                        Retry
                    </button>
                </div>
            )}

            {usingFallback && (
                <div className="w-full p-2 mb-2 bg-blue-100 border border-blue-300 rounded-md">
                    <span className="text-blue-800 text-sm">Using fallback options</span>
                </div>
            )}

            <div className="w-full relative">
                <Select
                    inputId={name}
                    name={name}
                    options={options}
                    value={currentOption}
                    onChange={handleReactSelectChange}
                    onInputChange={handleInputChange}
                    filterOption={() => true}
                    className="w-full"
                    classNamePrefix="react-select"
                    placeholder={placeholder || 'Select an option'}
                    isClearable
                    isSearchable
                    isMulti={multiSelect}
                    noOptionsMessage={() => {
                        if (useSearch && !searchTerm) {
                            return 'Type to search...';
                        }
                        return searchTerm ? `No results for "${searchTerm}"` : 'No options available';
                    }}
                    styles={{
                        ...styles,
                        control: (provided) => ({
                            ...provided,
                            ...styles.control,
                            height: multiSelect ? 'auto' : '48px',
                            minHeight: multiSelect ? '48px' : '48px',
                        }),
                        placeholder: (provided) => ({
                            ...provided,
                            ...styles.placeholder,
                        }),
                        multiValue: (provided) => ({
                            ...provided,
                            ...styles.multiValue,
                        }),
                        multiValueLabel: (provided) => ({
                            ...provided,
                            ...styles.multiValueLabel,
                        }),
                        multiValueRemove: (provided) => ({
                            ...provided,
                            ...styles.multiValueRemove,
                        }),
                        ...(multiSelect ? {
                            valueContainer: (provided) => ({
                                ...provided,
                                ...styles.valueContainer,
                            }),
                        } : {}),
                    }}
                />
                {loading && useSearch && (
                    <div className="absolute right-3 top-3 flex items-center pointer-events-none">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    </div>
                )}
            </div>

            {errors[name] && (
                <span className="text-red-500 text-sm mt-1">
                    {errors[name].message}
                </span>
            )}
        </div>
    );
};

export default LinkSelectField;